import {
  canonical,
  description,
  join,
  meta,
  openGraph,
  permanentRedirectAction,
  renderAction,
  title,
} from '@rea-argonaut/actions';
import { GetAction } from '@property.com.au/argonaut';
import { createUrqlClient, writeUrqlCacheToExchange } from '../../graphql/urqlClient';
import { getSlugFromPathname } from '../../helpers/getSlugFromPathname';
import { fetchStatePage } from '../graphql/fetchStatePage';
import { getCampaignData, getPageData } from '@realestate.com.au/iglu-data';
import { trackView } from '@rea-argonaut/analytics';
import { getPcaUserData } from '../../helpers/getPcaUserData';
import { UrlContextProvider } from '../../contexts/UrlContext/UrlContext';
import { Provider as UrqlProvider } from 'urql';
import { StatePageScreen } from '../screens/StatePageScreen';
import ogImageJpg from '../../assets/og-image.jpg';
import { getEntryRedirectUrl } from '../../helpers/getEntryRedirectUrl';
import { StatePageModalContextProvider } from '../contexts/StatePageModalContext/StatePageModalContext';

type PathParams = {
  state: 'nsw' | 'vic' | 'qld' | 'wa' | 'sa' | 'tas' | 'act' | 'nt';
};

export const getAction: GetAction<PathParams> = async ({ url, user }) => {
  const redirectURL = getEntryRedirectUrl(url);
  if (redirectURL) {
    return permanentRedirectAction(redirectURL.href);
  }

  const urqlClient = await createUrqlClient();
  const { data, error } = await fetchStatePage({
    client: urqlClient,
    slug: getSlugFromPathname(url.pathname),
  });

  if (error?.networkError) {
    throw Error('Could not successfully connect to Experience API');
  }

  if (!data?.statePageBySlug) {
    throw Error('No state page data returned from Experience API');
  }

  await writeUrqlCacheToExchange();

  const {
    helmet: { title: pageTitle, description: pageDescription, canonical: pageCanonical, siteName },
  } = data.statePageBySlug;

  return renderAction(async () => {
    trackView([
      getPageData({
        pageType: 'state',
        siteSection: 'state_suburb_street',
        site: 'pca',
      }),
      getPcaUserData(user),
      getCampaignData(),
    ]);

    return {
      getElements: async () => {
        return {
          Screen: (
            <UrqlProvider value={urqlClient}>
              <UrlContextProvider url={url}>
                <StatePageModalContextProvider>
                  <StatePageScreen />
                </StatePageModalContextProvider>
              </UrlContextProvider>
            </UrqlProvider>
          ),
        };
      },
      getHead: async () =>
        join(
          title(pageTitle),
          description(pageDescription),
          canonical(pageCanonical),
          openGraph({
            property: 'og:locale',
            content: 'en_AU',
          }),
          openGraph({
            property: 'og:type',
            content: 'website',
          }),
          openGraph({
            property: 'og:title',
            content: pageTitle,
          }),
          openGraph({
            property: 'og:description',
            content: pageDescription,
          }),
          openGraph({
            property: 'og:url',
            content: pageCanonical,
          }),
          openGraph({
            property: 'og:site_name',
            content: siteName,
          }),
          openGraph({
            property: 'og:image',
            content: ogImageJpg,
          }),
          meta({
            name: 'robots',
            content: 'index, follow, noodp',
          }),
        ),
    };
  });
};
